import { useCallback } from 'react';
import { Edge, Node, Position, useReactFlow } from 'reactflow';

// @ts-ignore flextree isn't typed
import { flextree } from 'd3-flextree';
import { HierarchyNode, stratify } from 'd3-hierarchy';
import { merge } from 'lodash-es';

import { DESCRIPTION_LINE_COUNT_LIMIT, descriptionStyle } from '@components/action-card';
import { useSidebarState } from '@components/sidebar';
import { theme } from '@styles/theme';
import { getTemporaryElementDimensions } from '@utils/browser';
import { pxToRem, styleToString } from '@utils/converters';

import { JourneyActionValuesTypes } from 'api/types';

import { EdgeProps } from '../journey-builder';
import { JourneyNode } from '../node-types/types';

export { useNodes as useFlowNodes } from 'reactflow';

const getNodeElementHeight = (description = '') => {
  // Faking a node to calculate the height of one
  const innerHtml = `<div style="${styleToString({
    width: pxToRem(320),
    border: '2px solid black',
    lineHeight: pxToRem(20),
    padding: `${pxToRem(8)} ${pxToRem(8)} ${pxToRem(12)} ${pxToRem(16)}`,
    font: theme.font.regular14,
  })}">
      <div style="${styleToString({ height: pxToRem(40), marginBottom: pxToRem(2) })}"></div>
      <div style="${styleToString(descriptionStyle)}
      ">${description.replaceAll('\n', '<br />')}</div>
    </div>`;

  const { height } = getTemporaryElementDimensions(innerHtml);

  return height;
};

type LoopNode = {
  child: string;
  parent: string;
  nodeDescription: string;
  description?: string;
};

const layoutWidth = 352;

const layout = flextree<Node>().nodeSize((node: Node) => {
  // @ts-ignore node does not have children according to the type...
  const isLeaf = node.children && node.children[0].data.type === 'hidden';

  const height = getNodeHeight(node);

  const extraHeight = node.data.type === 'break_loop' ? 8 : 0;

  const layoutHeight = isLeaf ? height + 12 : height + 35;

  if (
    node.data.hidden ||
    // @ts-ignore-next-line
    (node.data.type === 'hidden' && node.parent.data.type === 'journey_branch')
  ) {
    return [0, 0];
  }

  if (node.data.type === 'journey_triggers') {
    if (node.data.data.values.isTriggersCollapsed) {
      return [layoutWidth, 132];
    }
    // These numbers could be more fine tuned, but this is good enough for now
    return [layoutWidth, 208 + node.data.data.values.triggersCount * 76];
  }

  // @ts-ignore-next-line children is not typed
  if (node.data.type === 'branch_base' && node.children?.[0]?.data.type === 'hidden') {
    return [layoutWidth, 49];
  }

  switch (node.data.type) {
    case 'journey_start':
    case 'journey_merge':
      return [layoutWidth, 100];
    case 'journey_branch':
      return [layoutWidth, 144];
    case 'branch_base':
      return [layoutWidth, 71];
    case 'hidden':
      return [192, 74];
    case 'ghost':
      return [100, layoutHeight];
    default:
      return [layoutWidth, layoutHeight + extraHeight];
  }
});

export const getNodeHeight = (node: Node) => {
  const initialDescription = node.data?.data?.description ?? '';
  let description = '';

  if (node.data.data.isConfigured) {
    description = (node.data?.data?.nodeDescription ?? '') as string;
  }
  const defaultHeight = () => getNodeElementHeight(description);

  const conditionHeight = () => {
    // Condition has different rules than the rest, as each condition
    // should only take up one line in the UI. However we still want to
    // keep the entire description there for when that's used elsewhere,
    // so changing the description before we get to here probably isn't
    // the solution we want. I don't like this solution either but I'm
    // not seeing a better one atm. -siggirh

    let desc = description.split('\n');
    // The UI shows `X more...` if there are many conditions
    const hasMore = desc.length > DESCRIPTION_LINE_COUNT_LIMIT;
    const extra = hasMore ? 20 : 0;
    desc = desc.slice(0, hasMore ? DESCRIPTION_LINE_COUNT_LIMIT : DESCRIPTION_LINE_COUNT_LIMIT + 1);

    // 1 character for each line - don't need more and dont want it to wrap
    const elementText = desc.map((d) => d[0]).join('\n');
    return getNodeElementHeight(elementText) + extra;
  };
  switch (node.data.type) {
    case 'condition_v2':
      if (Boolean(initialDescription.trim())) return defaultHeight();
      return conditionHeight();
    case 'break_loop':
    case 'condition':
      return conditionHeight();
    default:
      return defaultHeight();
  }
};

const mapNodePositions = (nodes: Node[]) => {
  const sortChildren = (a: HierarchyNode<Node>, b: HierarchyNode<Node>) => {
    if (a.data.type === 'hidden' || b.data.type === 'ghost') {
      return 1;
    }
    if (b.data.type === 'hidden' || a.data.type === 'ghost') {
      return -1;
    }
    const order: string[] = a.parent?.data?.data?.values?.order ?? [];
    return order.indexOf(a.id ?? '') - order.indexOf(b.id ?? '');
  };
  const tree = stratify<Node>()(nodes).sort(sortChildren);

  const root = layout(tree);

  return nodes.map((node) => {
    // find the node in the tree with the same id and get its coordinates
    const { x, y } = root.find((d: Node) => d.id === node.id) || node.position;
    const adjustedX = node.type === 'ghost' ? x + 175 : x;
    const parentNode = root.find((d: Node) => d.id === node.parentId);
    const parentX = parentNode ? parentNode.x : 0;
    const parentY = parentNode ? parentNode.y : 0;

    return {
      ...node,
      sourcePosition: node.sourcePosition,
      targetPosition: node.targetPosition,
      position: { x: adjustedX - parentX, y: y - parentY },
      width: 320,
    };
  });
};

export const generateEdgeId = (source: EdgeProps['source'], target?: EdgeProps['target']) =>
  `e-${source}${target ? `-${target}` : ''}`;

export const useNodes = () => {
  const { getNodes, setNodes: setFlowNodes, setEdges } = useReactFlow();

  const getValidNodes = useCallback(
    (nodes?: JourneyNode[]) => {
      return (nodes ?? (getNodes() as JourneyNode[])).filter((node) => node.type !== 'hidden');
    },
    [getNodes]
  );

  const getNode = useCallback(
    (nodeId: JourneyNode['id']) => {
      return getValidNodes().find((node) => node.id === nodeId);
    },
    [getValidNodes]
  );

  const getValidNodesMap = () => {
    const nodes = getValidNodes();
    return nodes.reduce((acc, node) => {
      acc[node.id] = node;
      return acc;
    }, {} as Record<JourneyNode['id'], JourneyNode>);
  };

  const { activeId } = useSidebarState();
  const selectedNode = getValidNodes().find((node) => node.id === activeId) as JourneyNode;

  const updateSelectedNode = (changes: Partial<JourneyNode>) => {
    const updated = merge(selectedNode, changes);
    const nodes = getValidNodes();
    const selectedIndex = nodes.findIndex((n) => n.id === updated.id);
    if (selectedIndex > -1) {
      nodes[selectedIndex] = updated;
    }
    setNodes(nodes);
  };

  const updateSelectedNodeData = (changes: Partial<JourneyNode['data']>) =>
    updateSelectedNode({ data: { ...selectedNode.data, ...changes } });

  const setNodes = (nodes: JourneyNode[], skipHidden: boolean = false) => {
    const newEdges: Edge[] = [];
    const childNodes: JourneyNode[] = [];
    const validNodes = getValidNodes(nodes);

    const createHiddenNode = (nodeId: string, childIndex: number) => {
      if (!skipHidden) {
        const childId = `${nodeId}-${childIndex}-hidden`;
        childNodes.push({
          id: childId,
          type: 'hidden',
          width: 1,
          height: 1,
          parentId: nodeId,
          data: {
            nodeDescription: '',
            values: {},
            isConfigured: true,
          },
        });
        newEdges.push({
          id: generateEdgeId(childId),
          source: nodeId,
          target: childId,
          zIndex: 1,
        });
      }
    };

    const loopGhostNodes: JourneyNode[] = [];
    const createLoopGhostNodes = (loopActionId: string, breakLoopNode: JourneyNode) => {
      let currentNode = breakLoopNode;
      const hierarchy: LoopNode[] = [];
      while (currentNode) {
        if (currentNode.id === loopActionId) {
          break;
        }
        const ghostId = `${currentNode.id}-ghost`;
        const parentId = `${currentNode.parentId}${
          currentNode.parentId === loopActionId ? '' : '-ghost'
        }`;
        hierarchy.push({
          child: ghostId,
          parent: parentId,
          nodeDescription: currentNode.data?.nodeDescription ?? '',
          description: currentNode.data?.description ?? '',
        });
        const currentParentId = currentNode.parentId;
        currentNode = validNodes.find((n) => n.id === currentParentId) as JourneyNode;
      }
      hierarchy.forEach(({ child, parent, description, nodeDescription }) => {
        loopGhostNodes.push({
          id: child,
          type: 'ghost',
          width: 1,
          height: 1,
          targetPosition: parent === loopActionId ? Position.Top : Position.Bottom,
          sourcePosition:
            parent === loopActionId && hierarchy.length > 1 ? Position.Top : Position.Bottom,
          parentId: parent,
          data: {
            nodeDescription,
            description,
            values: {},
            isConfigured: true,
          },
        });
        newEdges.push({
          id: generateEdgeId(child),
          source: parent,
          target: child,
          type: 'loopEdge',
        });
      });
      // Small hax to create an edge through the break loop node if it's the only node
      if (hierarchy.length === 1) {
        newEdges.push({
          id: generateEdgeId(hierarchy[0].child, '-self'),
          source: hierarchy[0].child,
          target: hierarchy[0].child,
          type: 'loopEdge',
        });
      }
      const lastNodes = hierarchy
        .filter((node) => !hierarchy.some((item) => item.parent === node.child))
        .map((node) => node.child);
      lastNodes.forEach((lastNode) => {
        newEdges.push({
          id: generateEdgeId(lastNode, 'break-loop'),
          source: breakLoopNode.id,
          target: lastNode,
          type: 'loopEdge',
          zIndex: -1,
        });
      });
    };

    const getDependantSource = (shortId: JourneyNode['id']) => {
      const node = nodes.find((currentNode) => currentNode.id === shortId);

      if (!node?.hidden) {
        return node?.id ?? '';
      }

      const getParentNode = (node: JourneyNode) => {
        if (node) {
          return nodes.find((currentNode) => currentNode.id === node.parentId);
        }
        return undefined;
      };

      const findAncestor = (currentNode?: JourneyNode) => {
        while (currentNode) {
          if (!currentNode.hidden && ['branch_base', 'journey_branch'].includes(currentNode.type)) {
            return currentNode;
          }
          currentNode = getParentNode(currentNode);
        }
        return undefined;
      };

      return findAncestor(getParentNode(node))?.id || '';
    };

    validNodes.forEach((node) => {
      if (node.type === 'ghost' || node.hidden) {
        return;
      }
      if (node.type !== 'journey_start') {
        const source = node.parentId ?? 'start';
        const target = node.id;
        const edge = {
          id: generateEdgeId(source, target),
          source,
          target,
        };
        newEdges.push(edge);
        if (node.type === 'break_loop') {
          const values = node.data.values as JourneyActionValuesTypes['break_loop'];
          if (values?.loopAction) {
            createLoopGhostNodes(values.loopAction, node);
          }
        }
        if (node.dependantIds?.length) {
          node.dependantIds.forEach((dependantId) => {
            const source = getDependantSource(dependantId);
            const additionalEdge = {
              id: generateEdgeId(dependantId, target),
              source,
              target,
              animated: true,
              type: 'journeyExtraEdge',
            };
            newEdges.push(additionalEdge);
          });
        }

        if (
          (node.type === 'journey_branch' && !node.data.isCollapsed) ||
          ((node.type !== 'branch_base' || !node.data.isCollapsed) && node.data.childrenCount === 0)
        ) {
          createHiddenNode(node.id, 0);
        }
        // If only two nodes, it is the triggers node and the start node
      } else if (validNodes.length === 2) {
        createHiddenNode(node.id, 0);
      }
    });
    const newNodes = mapNodePositions([
      ...nodes.filter((node) => node.type !== 'ghost'),
      ...childNodes,
      ...loopGhostNodes,
    ] as Node[]);
    setFlowNodes(newNodes);
    setEdges(newEdges);
  };

  const removeNode = (nodeId: JourneyNode['id']) => {
    const filteredNodes = getValidNodes().filter((node) => node.id !== nodeId);
    setNodes(filteredNodes);
  };

  return {
    getValidNodes,
    getValidNodesMap,
    getNode,
    removeNode,
    updateSelectedNode,
    updateSelectedNodeData,
    setNodes,
    selectedNode,
  };
};

export type NodesState = ReturnType<typeof useNodes>;
